import {
  applyLegacyInventoryTransferToCraftingTable,
  applyLegacyInventoryTransferToPartyPool,
  applyShopPurchaseToLegacyInventory,
  materializePartyPoolItemFromLegacyInventoryItem,
  materializeTransferredLegacyInventoryItem,
  materializeLegacyInventoryItemFromCraftResult,
  materializeLegacyInventoryItemFromLootCatalogItem,
} from './materialize';
import {
  activateLegacyInventoryAbilityRuntime,
  consumeLegacyInventoryItemRuntime,
  parseLegacyAbilityEffectLine,
  parseLegacyConsumableResourceEffect,
  toggleLegacyInventoryItemEquipRuntime,
} from './runtime';
import { canStackLegacyInventoryItems, stackLegacyInventoryItem } from './stacking';

const DEFAULT_CURRENCY_TIERS = [
  { id: 'copper', name: 'Copper', symbol: 'cp', multiplier: 1, baseId: null },
  { id: 'silver', name: 'Silver', symbol: 'sp', multiplier: 10, baseId: 'copper' },
  { id: 'gold', name: 'Gold', symbol: 'gp', multiplier: 10, baseId: 'silver' },
  { id: 'platinum', name: 'Platinum', symbol: 'pp', multiplier: 10, baseId: 'gold' },
];

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, innerValue]) => [key, cloneValue(innerValue)]),
    );
  }
  return value;
};

const normalizeMoveQuantity = (requested, available) => {
  const haveQty = Math.max(1, Number(available) || 1);
  if (requested == null) return haveQty;
  return Math.max(1, Math.min(haveQty, Math.trunc(Number(requested)) || 1));
};

const normalizeConsumedIngredients = (ingredients = []) => (
  Array.isArray(ingredients)
    ? ingredients
        .map((ingredient) => ({
          itemId: String(ingredient?.itemId || ingredient?.iid || '').trim(),
          quantity: Math.max(0, Math.trunc(Number(ingredient?.quantity ?? ingredient?.qty) || 0)),
        }))
        .filter((ingredient) => ingredient.itemId && ingredient.quantity > 0)
    : []
);

const sanitizeInventoryEditorUpdates = (updates = {}) => {
  if (!updates || typeof updates !== 'object') return {};
  const { id, _showAdvanced, ...cleanUpdates } = updates;
  const clean = cloneValue(cleanUpdates);
  if (clean.quantity !== undefined) {
    clean.quantity = Math.max(1, Math.trunc(Number(clean.quantity)) || 1);
  }
  return clean;
};

export const applyCharInventoryItemAdd = (
  charData = {},
  {
    item,
  } = {},
) => {
  if (!item || typeof item !== 'object') return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const itemId = item.id ? String(item.id) : '';
  if (!itemId || inventory.some((candidate) => candidate?.id === item.id)) return null;

  const itemToAdd = cloneValue({
    ...item,
    quantity: Math.max(1, Math.trunc(Number(item.quantity)) || 1),
  });

  return {
    item: itemToAdd,
    nextCharData: {
      ...charData,
      inventory: [...inventory, itemToAdd],
    },
  };
};

export const applyCharInventoryItemUpdate = (
  charData = {},
  {
    itemEntryId,
    updates,
  } = {},
) => {
  if (!itemEntryId) return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  const cleanUpdates = sanitizeInventoryEditorUpdates(updates);
  const updatedItem = {
    ...item,
    ...cleanUpdates,
    id: item.id,
  };

  return {
    item: updatedItem,
    previousItem: item,
    nextCharData: {
      ...charData,
      inventory: inventory.map((candidate) => (
        candidate?.id === item.id ? updatedItem : candidate
      )),
    },
  };
};

export const applyCharInventoryItemFavoriteToggle = (
  charData = {},
  {
    itemEntryId,
    favorite,
    expectedFavorited,
  } = {},
) => {
  if (!itemEntryId) return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  const currentFavorited = item.favorited === true;
  if (typeof expectedFavorited === 'boolean' && currentFavorited !== expectedFavorited) return null;

  const nextFavorited = typeof favorite === 'boolean' ? favorite : !currentFavorited;
  if (currentFavorited === nextFavorited) {
    return {
      item,
      previousItem: item,
      favorited: nextFavorited,
      changed: false,
      nextCharData: charData,
    };
  }

  const result = applyCharInventoryItemUpdate(charData, {
    itemEntryId,
    updates: { favorited: nextFavorited },
  });
  return result
    ? {
        ...result,
        favorited: result.item?.favorited === true,
        changed: true,
      }
    : null;
};

export const applyCharInventoryItemRemove = (
  charData = {},
  {
    itemEntryId,
  } = {},
) => {
  if (!itemEntryId) return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item || item.equipped === true) return null;

  return {
    removedItem: item,
    nextCharData: {
      ...charData,
      inventory: inventory.filter((candidate) => candidate?.id !== item.id),
    },
  };
};

export const applyCharInventoryItemEquipToggle = (
  charData = {},
  {
    itemEntryId,
    expectedEquipped,
    now = new Date().toISOString(),
  } = {},
) => {
  if (!itemEntryId) return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  const currentEquipped = item.equipped === true;
  if (typeof expectedEquipped === 'boolean' && currentEquipped === expectedEquipped) {
    return {
      item,
      newEquipped: currentEquipped,
      changed: false,
      updates: {},
      nextCharData: charData,
    };
  }

  const result = toggleLegacyInventoryItemEquipRuntime({
    char: charData,
    inventory,
    itemId: itemEntryId,
    now,
  });
  if (!result) return null;

  if (typeof expectedEquipped === 'boolean' && result.newEquipped !== expectedEquipped) {
    return null;
  }

  return {
    ...result,
    changed: true,
    nextCharData: {
      ...charData,
      ...result.updates,
    },
  };
};

export const applyCharInventoryItemConsume = (
  charData = {},
  {
    itemEntryId,
    expectedQuantity,
    expectedUsesCurrent,
    parseEffect = parseLegacyConsumableResourceEffect,
    getEffectiveMaxHp,
    durabilityEnabled = true,
    manaEnabled = true,
    now = new Date().toISOString(),
  } = {},
) => {
  if (!itemEntryId) return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  if (expectedQuantity !== undefined && expectedQuantity !== null) {
    const currentQuantity = Math.max(1, Math.trunc(Number(item.quantity) || 1));
    const expectedQuantityNumber = Math.max(1, Math.trunc(Number(expectedQuantity) || 1));
    if (currentQuantity !== expectedQuantityNumber) return null;
  }

  if (expectedUsesCurrent !== undefined && expectedUsesCurrent !== null) {
    if (!item.uses || typeof item.uses !== 'object') return null;
    const currentUses = Number(item.uses.current ?? item.uses.cur ?? 0);
    const expectedUses = Number(expectedUsesCurrent);
    if (!Number.isFinite(currentUses) || !Number.isFinite(expectedUses) || currentUses !== expectedUses) {
      return null;
    }
  }

  const result = consumeLegacyInventoryItemRuntime({
    char: charData,
    inventory,
    item,
    parseEffect,
    getEffectiveMaxHp,
    durabilityEnabled,
    manaEnabled,
    now,
  });
  if (!result) return null;

  const nextCharData = {
    ...charData,
    ...result.updates,
  };
  const nextInventory = Array.isArray(nextCharData.inventory) ? nextCharData.inventory : inventory;

  return {
    item,
    updates: result.updates,
    receiptLines: result.receiptLines || [],
    deltas: result.deltas || { hpDelta: 0, manaDelta: 0, duraDelta: 0 },
    removedFromInventory: !nextInventory.some((candidate) => candidate?.id === itemEntryId),
    nextCharData,
  };
};

export const applyCharInventoryItemAbilityActivation = (
  charData = {},
  {
    itemEntryId,
    itemDefinitionId,
    abilityId,
    expectedUsesCurrent,
    expectedUsesMax,
    resolveEffectLine = parseLegacyAbilityEffectLine,
    now = new Date().toISOString(),
  } = {},
) => {
  if (!itemEntryId || !abilityId) return null;
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  if (itemDefinitionId !== undefined && itemDefinitionId !== null) {
    const expectedDefinitionId = String(itemDefinitionId);
    const currentDefinitionId = String(item.itemId || item.definitionId || '');
    if (currentDefinitionId && currentDefinitionId !== expectedDefinitionId) return null;
  }

  const ability = Array.isArray(item.abilities)
    ? item.abilities.find((candidate) => candidate?.id === abilityId)
    : null;
  if (!ability) return null;

  if (expectedUsesCurrent !== undefined && expectedUsesCurrent !== null) {
    if (!ability.uses || typeof ability.uses !== 'object') return null;
    const currentUses = Number(ability.uses.current ?? ability.uses.cur ?? ability.uses.max ?? 0);
    const expectedUses = Number(expectedUsesCurrent);
    if (!Number.isFinite(currentUses) || !Number.isFinite(expectedUses) || currentUses !== expectedUses) {
      return null;
    }
  }

  if (expectedUsesMax !== undefined && expectedUsesMax !== null) {
    if (!ability.uses || typeof ability.uses !== 'object') return null;
    const maxUses = Number(ability.uses.max ?? ability.uses.current ?? ability.uses.cur ?? 0);
    const expectedMax = Number(expectedUsesMax);
    if (!Number.isFinite(maxUses) || !Number.isFinite(expectedMax) || maxUses !== expectedMax) {
      return null;
    }
  }

  const result = activateLegacyInventoryAbilityRuntime({
    char: charData,
    inventory,
    itemId: itemEntryId,
    abilityId,
    resolveEffectLine,
    now,
  });
  if (!result) return null;

  const nextCharData = {
    ...charData,
    ...result.updates,
  };
  const nextInventory = Array.isArray(nextCharData.inventory) ? nextCharData.inventory : inventory;
  const nextItem = nextInventory.find((candidate) => candidate?.id === itemEntryId) || item;
  const nextAbility = Array.isArray(nextItem.abilities)
    ? nextItem.abilities.find((candidate) => candidate?.id === abilityId)
    : null;
  const abilityUsesBefore = ability.uses
    ? Number(ability.uses.current ?? ability.uses.cur ?? ability.uses.max ?? 0)
    : null;
  const abilityUsesAfter = nextAbility?.uses
    ? Number(nextAbility.uses.current ?? nextAbility.uses.cur ?? nextAbility.uses.max ?? 0)
    : abilityUsesBefore;

  return {
    item,
    ability,
    updates: result.updates,
    receiptLines: result.receiptLines || [],
    statusBuffsToAdd: result.statusBuffsToAdd || [],
    abilityUsesBefore: Number.isFinite(abilityUsesBefore) ? abilityUsesBefore : null,
    abilityUsesAfter: Number.isFinite(abilityUsesAfter) ? abilityUsesAfter : null,
    nextCharData,
    changed: Object.keys(result.updates || {}).length > 0,
  };
};

const normalizeCurrencyTiers = (tiers = DEFAULT_CURRENCY_TIERS) => (
  Array.isArray(tiers) && tiers.length > 0 ? tiers : DEFAULT_CURRENCY_TIERS
);

const currencyToBaseValue = (amount, currencyId, tiers = DEFAULT_CURRENCY_TIERS) => {
  const normalizedTiers = normalizeCurrencyTiers(tiers);
  const tiersById = new Map(normalizedTiers.map((candidate) => [candidate.id, candidate]));
  const tier = tiersById.get(currencyId);
  const numericAmount = Number(amount) || 0;
  if (!tier) return numericAmount;

  let multiplier = Number(tier.multiplier) || 1;
  let currentTier = tier;
  while (currentTier.baseId) {
    const parentTier = tiersById.get(currentTier.baseId);
    if (!parentTier) break;
    multiplier *= Number(parentTier.multiplier) || 1;
    currentTier = parentTier;
  }

  const baseTier = normalizedTiers.find((candidate) => !candidate.baseId);
  if (baseTier && tier.id !== baseTier.id) {
    return numericAmount * (multiplier / (Number(baseTier.multiplier) || 1));
  }
  return numericAmount;
};

const walletToBaseValue = (wallet = {}, tiers = DEFAULT_CURRENCY_TIERS) => (
  Object.entries(wallet || {}).reduce((total, [currencyId, amount]) => (
    total + currencyToBaseValue(amount || 0, currencyId, tiers)
  ), 0)
);

const baseToWalletValue = (baseAmount, tiers = DEFAULT_CURRENCY_TIERS) => {
  const normalizedTiers = normalizeCurrencyTiers(tiers);
  const wallet = {};
  const tiersWithValue = normalizedTiers
    .map((tier) => ({
      ...tier,
      totalValue: currencyToBaseValue(1, tier.id, normalizedTiers),
    }))
    .sort((a, b) => b.totalValue - a.totalValue);
  let remaining = Math.max(0, Math.floor(Number(baseAmount) || 0));

  for (const tier of tiersWithValue) {
    if (tier.totalValue <= 0) continue;
    const count = Math.floor(remaining / tier.totalValue);
    wallet[tier.id] = count;
    remaining -= count * tier.totalValue;
  }

  return wallet;
};

const normalizeShopPurchaseQuantity = (quantity) => {
  const parsed = Math.trunc(Number(quantity));
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
};

const resolveShopListingId = (shopId, listing = {}) => (
  listing.listingId || listing.id || (listing.itemId ? `${shopId || 'shop'}:${listing.itemId}` : null)
);

const resolveAuthoritativeShopUnitPrice = (listing = {}) => {
  const parsed = Number(listing.price);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveInventoryEntryIdByProvenance = (inventory = [], expectedProvenance = {}) => {
  if (!Array.isArray(inventory) || !expectedProvenance || typeof expectedProvenance !== 'object') {
    return null;
  }
  const requiredKeys = ['sourceType', 'transactionId', 'shopId', 'listingId', 'definitionId'];
  const item = inventory.find((candidate) => {
    const provenance = candidate?.provenance;
    if (!provenance || typeof provenance !== 'object') return false;
    return requiredKeys.every((key) => provenance[key] === expectedProvenance[key]);
  });
  return item?.id || null;
};

const isShopAccessibleForPurchase = (shop = {}, { isDM = false, currentCharId = null } = {}) => {
  if (isDM) return true;
  if (!shop.isOpen || !shop.starred) return false;
  if (!Array.isArray(shop.visibleTo) || shop.visibleTo.length === 0) return true;
  return Boolean(currentCharId && shop.visibleTo.includes(currentCharId));
};

const isListingVisibleForPurchase = (listing = {}, { isDM = false } = {}) => (
  isDM || (listing.secrecy || 'visible') === 'visible'
);

const normalizeShopPurchaseLines = (purchases = []) => (
  (Array.isArray(purchases) ? purchases : [])
    .map((purchase) => ({
      ...purchase,
      listingId: purchase?.listingId || purchase?.id || null,
      itemId: purchase?.itemId || null,
      quantity: normalizeShopPurchaseQuantity(purchase?.quantity ?? purchase?.qty),
    }))
    .filter((purchase) => purchase.quantity > 0 && (purchase.listingId || purchase.itemId))
);

const defaultLootEncounterIdFactory = () => `loot_enc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const defaultLootDropIdFactory = ({ encounterId, definitionId, index }) => (
  `${encounterId || 'loot_enc'}:drop:${index + 1}:${definitionId || 'item'}`
);

const normalizeLootDropQuantity = (quantity) => {
  const parsed = Math.trunc(Number(quantity));
  return Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
};

const normalizeLootDropDefinitionId = (drop = {}) => (
  drop.definitionId || drop.itemId || drop.id || null
);

const resolveLootCurrencyId = (item = {}, tiers = DEFAULT_CURRENCY_TIERS) => {
  if (item.currencyId) return item.currencyId;
  if (item.currencyTierId) return item.currencyTierId;
  if (item.walletCurrencyId) return item.walletCurrencyId;
  if (item._raw?.currencyId) return item._raw.currencyId;

  const normalizedTiers = normalizeCurrencyTiers(tiers);
  if (normalizedTiers.some((tier) => tier.id === 'gold')) return 'gold';
  return normalizedTiers[normalizedTiers.length - 1]?.id || 'gold';
};

const resolveLootCurrencyAward = (item = {}, quantity = 1, tiers = DEFAULT_CURRENCY_TIERS) => {
  const normalizedTiers = normalizeCurrencyTiers(tiers);
  const currencyId = resolveLootCurrencyId(item, normalizedTiers);
  const amount = Number(
    item.currencyAmount
      ?? item.amount
      ?? item.basePrice
      ?? 0,
  ) || 0;
  const awardQuantity = normalizeLootDropQuantity(quantity);
  const baseAmount = currencyToBaseValue(amount * awardQuantity, currencyId, normalizedTiers);

  return {
    currencyId,
    amount,
    quantity: awardQuantity,
    baseAmount: Math.max(0, Math.floor(baseAmount)),
  };
};

const buildLootEncounterCompatibilityMaps = (drops = []) => {
  const visibleToPlayers = {};
  const playerClaims = {};
  const awardedItems = {};
  const revealedQtys = {};
  const revealedItemIds = [];

  drops.forEach((drop) => {
    revealedItemIds.push(drop.definitionId);
    visibleToPlayers[drop.dropId] = drop.visibleToPlayers === true;
    revealedQtys[drop.definitionId] = (revealedQtys[drop.definitionId] || 0) + normalizeLootDropQuantity(drop.quantity);

    if (drop.playerClaims?.length > 0) {
      playerClaims[drop.dropId] = [...drop.playerClaims];
    }
    if (drop.awardedTo) {
      awardedItems[drop.dropId] = drop.awardedTo;
    }
  });

  return {
    revealedItemIds,
    revealedQtys,
    visibleToPlayers,
    playerClaims,
    awardedItems,
  };
};

const writeLootEncounterDrops = (encounter = {}, drops = []) => ({
  ...(encounter || {}),
  ...buildLootEncounterCompatibilityMaps(drops),
  drops: drops.map((drop) => ({
    ...cloneValue(drop),
    quantity: normalizeLootDropQuantity(drop.quantity),
    playerClaims: Array.isArray(drop.playerClaims) ? [...drop.playerClaims] : [],
    visibleToPlayers: drop.visibleToPlayers === true,
    awardedTo: drop.awardedTo || null,
  })),
});

const normalizeLootSelectedDrop = (drop, index, qtys = {}) => {
  if (typeof drop === 'string') {
    return {
      definitionId: drop,
      quantity: normalizeLootDropQuantity(qtys[drop]),
      lootTableRowIndex: index,
    };
  }

  const definitionId = normalizeLootDropDefinitionId(drop);
  if (!definitionId) return null;

  return {
    ...cloneValue(drop),
    definitionId,
    quantity: normalizeLootDropQuantity(drop.quantity ?? drop.qty ?? qtys[definitionId]),
    lootTableRowIndex: drop.lootTableRowIndex ?? drop.index ?? index,
  };
};

export const createLootEncounterRuntime = ({
  enemyId = null,
  selectedDrops = [],
  selectedItemIds = null,
  qtys = {},
  itemSources = {},
  encounterId = null,
  encounterIdFactory = defaultLootEncounterIdFactory,
  dropIdFactory = defaultLootDropIdFactory,
  rolledAt = new Date().toISOString(),
} = {}) => {
  const resolvedEncounterId = encounterId || encounterIdFactory({ enemyId });
  const sourceDrops = Array.isArray(selectedDrops) && selectedDrops.length > 0
    ? selectedDrops
    : (Array.isArray(selectedItemIds) ? selectedItemIds : []);
  const drops = sourceDrops
    .map((drop, index) => normalizeLootSelectedDrop(drop, index, qtys))
    .filter(Boolean)
    .map((drop, index) => {
      const snapshot = itemSources?.[drop.definitionId]
        ? cloneValue(itemSources[drop.definitionId])
        : null;
      const dropId = drop.dropId || drop.id || dropIdFactory({
        encounterId: resolvedEncounterId,
        enemyId,
        definitionId: drop.definitionId,
        index,
        lootTableRowIndex: drop.lootTableRowIndex,
      });

      return {
        dropId,
        definitionId: drop.definitionId,
        quantity: normalizeLootDropQuantity(drop.quantity),
        visibleToPlayers: false,
        playerClaims: [],
        awardedTo: null,
        snapshot,
        sourceType: snapshot?._source || drop.sourceType || null,
        lootTableRowIndex: drop.lootTableRowIndex,
        provenance: {
          sourceType: 'loot-drop',
          encounterId: resolvedEncounterId,
          enemyId,
          definitionId: drop.definitionId,
          dropId,
          lootTableRowIndex: drop.lootTableRowIndex,
          rolledAt,
        },
      };
    });

  return writeLootEncounterDrops({
    encounterId: resolvedEncounterId,
    enemyId,
    xpDistributed: false,
  }, drops);
};

export const normalizeLootEncounterDrops = (encounter = {}) => {
  if (!encounter) return [];
  if (Array.isArray(encounter.drops) && encounter.drops.length > 0) {
    return encounter.drops
      .map((drop, index) => {
        const definitionId = normalizeLootDropDefinitionId(drop);
        if (!definitionId) return null;
        const dropId = drop.dropId || drop.entryId || drop.id || `${definitionId}:${index + 1}`;

        return {
          ...cloneValue(drop),
          dropId,
          definitionId,
          quantity: normalizeLootDropQuantity(drop.quantity ?? drop.qty ?? encounter.revealedQtys?.[definitionId]),
          visibleToPlayers: drop.visibleToPlayers ?? encounter.visibleToPlayers?.[dropId] ?? encounter.visibleToPlayers?.[definitionId] ?? false,
          playerClaims: Array.isArray(drop.playerClaims)
            ? [...drop.playerClaims]
            : [...(encounter.playerClaims?.[dropId] || encounter.playerClaims?.[definitionId] || [])],
          awardedTo: drop.awardedTo ?? encounter.awardedItems?.[dropId] ?? encounter.awardedItems?.[definitionId] ?? null,
          snapshot: drop.snapshot ? cloneValue(drop.snapshot) : null,
          lootTableRowIndex: drop.lootTableRowIndex ?? index,
        };
      })
      .filter(Boolean);
  }

  const seenDefinitionCounts = {};
  return (Array.isArray(encounter.revealedItemIds) ? encounter.revealedItemIds : [])
    .map((definitionId, index) => {
      if (!definitionId) return null;
      const seen = seenDefinitionCounts[definitionId] || 0;
      seenDefinitionCounts[definitionId] = seen + 1;
      const dropId = seen === 0 ? definitionId : `${definitionId}:${seen + 1}`;

      return {
        dropId,
        definitionId,
        quantity: normalizeLootDropQuantity(encounter.revealedQtys?.[definitionId]),
        visibleToPlayers: encounter.visibleToPlayers?.[dropId] ?? encounter.visibleToPlayers?.[definitionId] ?? false,
        playerClaims: [...(encounter.playerClaims?.[dropId] || encounter.playerClaims?.[definitionId] || [])],
        awardedTo: encounter.awardedItems?.[dropId] ?? encounter.awardedItems?.[definitionId] ?? null,
        snapshot: null,
        lootTableRowIndex: index,
        provenance: {
          sourceType: 'legacy-loot-drop',
          enemyId: encounter.enemyId || null,
          definitionId,
          dropId,
        },
      };
    })
    .filter(Boolean);
};

const normalizePublishedLootDropRows = (publishResult = {}) => (
  Array.isArray(publishResult?.drops)
    ? publishResult.drops
      .map((drop, index) => {
        const definitionId = normalizeLootDropDefinitionId(drop);
        const dropId = drop?.dropId || drop?.id || drop?.entryId || null;
        if (!definitionId || !dropId) return null;
        const lootTableRowIndex = Number.isFinite(Number(drop.lootTableRowIndex))
          ? Number(drop.lootTableRowIndex)
          : index;
        return {
          ...cloneValue(drop),
          dropId,
          clientDropId: drop.clientDropId || null,
          definitionId,
          quantity: normalizeLootDropQuantity(drop.quantity ?? drop.qty),
          visibleToPlayers: drop.visibleToPlayers === true,
          playerClaims: Array.isArray(drop.playerClaims) ? [...drop.playerClaims] : [],
          awardedTo: drop.awardedTo || null,
          version: Number.isFinite(Number(drop.version)) ? Number(drop.version) : null,
          lootTableRowIndex,
        };
      })
      .filter(Boolean)
    : []
);

export const reconcileLootEncounterDropIdsFromPublishResult = (encounter = {}, publishResult = {}) => {
  const publishedDrops = normalizePublishedLootDropRows(publishResult);
  if (!encounter || publishedDrops.length === 0) return encounter;

  const usedPublishedIndexes = new Set();
  const currentDrops = normalizeLootEncounterDrops(encounter);
  const nextDrops = currentDrops.map((drop, index) => {
    const matchIndex = publishedDrops.findIndex((candidate, candidateIndex) => {
      if (usedPublishedIndexes.has(candidateIndex)) return false;
      if (candidate.clientDropId && candidate.clientDropId === drop.dropId) return true;
      if (candidate.dropId === drop.dropId) return true;
      return candidate.definitionId === drop.definitionId
        && candidate.lootTableRowIndex === (drop.lootTableRowIndex ?? index);
    });

    if (matchIndex < 0) return drop;
    usedPublishedIndexes.add(matchIndex);
    const published = publishedDrops[matchIndex];
    const nextDropId = published.dropId || drop.dropId;
    const provenance = {
      ...(drop.provenance || {}),
      dropId: nextDropId,
    };
    if (published.clientDropId && published.clientDropId !== nextDropId) {
      provenance.clientDropId = published.clientDropId;
    }

    return {
      ...drop,
      dropId: nextDropId,
      definitionId: published.definitionId || drop.definitionId,
      quantity: normalizeLootDropQuantity(published.quantity ?? drop.quantity),
      visibleToPlayers: published.visibleToPlayers === true,
      playerClaims: Array.isArray(published.playerClaims) ? [...published.playerClaims] : (drop.playerClaims || []),
      awardedTo: published.awardedTo || drop.awardedTo || null,
      version: published.version ?? drop.version,
      lootTableRowIndex: published.lootTableRowIndex ?? drop.lootTableRowIndex ?? index,
      provenance,
    };
  });

  return writeLootEncounterDrops({
    ...(encounter || {}),
    encounterId: publishResult.encounterId || encounter.encounterId,
    version: Number.isFinite(Number(publishResult.version))
      ? Number(publishResult.version)
      : encounter.version,
  }, nextDrops);
};

const updateLootDropInEncounter = (encounter = {}, dropId, updater) => {
  if (!dropId || typeof updater !== 'function') return null;
  const drops = normalizeLootEncounterDrops(encounter);
  const dropIndex = drops.findIndex((drop) => drop.dropId === dropId);
  if (dropIndex < 0) return null;

  const currentDrop = drops[dropIndex];
  const nextDrop = updater(currentDrop);
  if (!nextDrop) return null;

  const nextDrops = drops.map((drop, index) => (
    index === dropIndex ? nextDrop : drop
  ));

  return {
    encounter: writeLootEncounterDrops(encounter, nextDrops),
    drop: nextDrop,
    previousDrop: currentDrop,
    changed: stableSerializeForLootDrop(currentDrop) !== stableSerializeForLootDrop(nextDrop),
  };
};

const stableSerializeForLootDrop = (drop) => JSON.stringify({
  awardedTo: drop.awardedTo || null,
  playerClaims: [...(drop.playerClaims || [])].sort(),
  visibleToPlayers: drop.visibleToPlayers === true,
});

export const setLootDropVisibilityInEncounter = (
  encounter = {},
  { dropId, visible = null } = {},
) => updateLootDropInEncounter(encounter, dropId, (drop) => ({
  ...drop,
  visibleToPlayers: visible == null ? !drop.visibleToPlayers : visible === true,
}));

export const revealAllLootDropsInEncounter = (encounter = {}) => {
  const drops = normalizeLootEncounterDrops(encounter).map((drop) => ({
    ...drop,
    visibleToPlayers: true,
  }));
  return {
    encounter: writeLootEncounterDrops(encounter, drops),
    changed: drops.some((drop) => drop.visibleToPlayers !== normalizeLootEncounterDrops(encounter).find((candidate) => candidate.dropId === drop.dropId)?.visibleToPlayers),
  };
};

export const claimLootDropInEncounter = (
  encounter = {},
  { dropId, playerId } = {},
) => updateLootDropInEncounter(encounter, dropId, (drop) => {
  if (!playerId || drop.awardedTo) return null;
  if (drop.playerClaims.includes(playerId)) return drop;
  return {
    ...drop,
    playerClaims: [...drop.playerClaims, playerId],
  };
});

export const withdrawLootClaimInEncounter = (
  encounter = {},
  { dropId, playerId } = {},
) => updateLootDropInEncounter(encounter, dropId, (drop) => {
  if (!playerId || drop.awardedTo) return null;
  return {
    ...drop,
    playerClaims: drop.playerClaims.filter((candidate) => candidate !== playerId),
  };
});

export const awardLootDropInEncounter = (
  encounter = {},
  { dropId, playerId } = {},
) => updateLootDropInEncounter(encounter, dropId, (drop) => {
  if (!playerId) return null;
  if (drop.awardedTo && drop.awardedTo !== playerId) return null;
  return {
    ...drop,
    awardedTo: playerId,
  };
});

export const applyLootDropAwardToChar = (
  charData = {},
  encounter = {},
  itemSources = {},
  {
    dropId,
    currencyTiers = DEFAULT_CURRENCY_TIERS,
    transactionId = `loot_tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    acquiredAt = new Date().toISOString(),
    entryIdFactory,
    abilityIdFactory,
  } = {},
) => {
  const drop = normalizeLootEncounterDrops(encounter).find((candidate) => candidate.dropId === dropId);
  if (!drop) return null;

  const item = drop.snapshot || itemSources?.[drop.definitionId] || null;
  if (!item) return null;

  const provenance = {
    sourceType: 'loot-award',
    transactionId,
    encounterId: encounter.encounterId || null,
    enemyId: encounter.enemyId || null,
    dropId: drop.dropId,
    definitionId: drop.definitionId,
    quantity: drop.quantity,
    awardedAt: acquiredAt,
  };

  if (item.category === 'currency') {
    const currencyAward = resolveLootCurrencyAward(item, drop.quantity, currencyTiers);
    const walletBase = walletToBaseValue(charData.wallet || {}, currencyTiers);

    return {
      drop,
      item,
      provenance,
      currencyAward,
      inventoryItem: null,
      nextCharData: {
        ...charData,
        wallet: baseToWalletValue(walletBase + currencyAward.baseAmount, currencyTiers),
      },
    };
  }

  const inventoryItem = materializeLegacyInventoryItemFromLootCatalogItem(item, {
    quantity: drop.quantity,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
    lootSource: encounter.enemyId || null,
    extraFields: { provenance },
  });
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const stackResult = stackItemAndResolveDestinationId(inventory, inventoryItem);

  return {
    drop,
    item,
    provenance,
    currencyAward: null,
    inventoryItem,
    itemEntryId: stackResult.itemEntryId,
    nextCharData: {
      ...charData,
      inventory: stackResult.nextItems,
    },
  };
};

const consumeCraftingIngredients = (currentTable = {}, consumedIngredients = []) => {
  const tableInv = { ...((currentTable && currentTable.inv) || {}) };
  const normalizedIngredients = normalizeConsumedIngredients(consumedIngredients);

  for (const ingredient of normalizedIngredients) {
    const currentQty = Math.max(0, Number(tableInv[ingredient.itemId]) || 0);
    if (currentQty < ingredient.quantity) return null;
  }

  for (const ingredient of normalizedIngredients) {
    const nextQty = Math.max(0, (Number(tableInv[ingredient.itemId]) || 0) - ingredient.quantity);
    if (nextQty > 0) tableInv[ingredient.itemId] = nextQty;
    else delete tableInv[ingredient.itemId];
  }

  return {
    ...(currentTable || {}),
    inv: tableInv,
    customItems: Array.isArray(currentTable?.customItems)
      ? cloneValue(currentTable.customItems)
      : [],
  };
};

const findCraftingTableCustomItem = (table = {}, tableItemId) => (
  (Array.isArray(table?.customItems) ? table.customItems : [])
    .find((candidate) => candidate?.id === tableItemId) || null
);

const resolveCraftingTableSourceItem = (table = {}, tableItemId, fallbackResult = null) => {
  const customItem = findCraftingTableCustomItem(table, tableItemId);
  if (customItem) return customItem;
  if (fallbackResult && typeof fallbackResult === 'object') {
    return {
      id: tableItemId,
      name: fallbackResult.name || 'Item',
      icon: fallbackResult.icon || null,
      cat: fallbackResult.cat || null,
      _res: fallbackResult,
    };
  }
  return null;
};

const decrementCraftingTableRow = (table = {}, tableItemId, quantity = null) => {
  const tableInv = { ...((table && table.inv) || {}) };
  const haveQty = Math.max(0, Number(tableInv[tableItemId]) || 0);
  if (haveQty <= 0) return null;

  const moveQty = normalizeMoveQuantity(quantity, haveQty);
  const nextQty = haveQty - moveQty;
  if (nextQty > 0) tableInv[tableItemId] = nextQty;
  else delete tableInv[tableItemId];

  return {
    movedQuantity: moveQty,
    removedFromCraftingTable: nextQty <= 0,
    nextCraftingTable: {
      ...(table || {}),
      inv: tableInv,
      customItems: Array.isArray(table?.customItems)
        ? cloneValue(table.customItems)
        : [],
    },
  };
};

const materializeCraftingTableSourceForInventory = (
  tableItem = {},
  {
    quantity,
    fallbackResult = null,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
  } = {},
) => {
  if (tableItem?._invSnapshot) {
    return materializeTransferredLegacyInventoryItem(tableItem._invSnapshot, {
      quantity,
      sourceName: 'Crafting Table',
      entryIdFactory,
      abilityIdFactory,
      acquiredAt,
    });
  }

  const result = tableItem?._res || fallbackResult || tableItem;
  return materializeLegacyInventoryItemFromCraftResult(result, {
    quantity,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
  });
};

const stackItemAndResolveDestinationId = (items = [], incomingItem = null) => {
  if (!incomingItem) {
    return {
      nextItems: items,
      itemEntryId: null,
    };
  }
  const normalizedItems = Array.isArray(items) ? items : [];
  const existingItem = normalizedItems.find((candidate) => canStackLegacyInventoryItems(candidate, incomingItem));
  return {
    nextItems: stackLegacyInventoryItem(normalizedItems, incomingItem),
    itemEntryId: existingItem?.id || incomingItem.id || null,
  };
};

const materializeCraftingTableSourceForPartyPool = (
  poolItems = [],
  tableItem = {},
  {
    quantity,
    fallbackResult = null,
    entryIdFactory,
    abilityIdFactory,
    craftedEntryIdFactory,
    craftedAbilityIdFactory,
    acquiredAt,
  } = {},
) => {
  if (tableItem?._invSnapshot) {
    const partyItem = materializePartyPoolItemFromLegacyInventoryItem(tableItem._invSnapshot, {
      quantity,
      entryIdFactory,
      abilityIdFactory,
    });
    const result = stackItemAndResolveDestinationId(poolItems, partyItem);
    return {
      nextPartyItems: result.nextItems,
      partyItemEntryId: result.itemEntryId,
    };
  }

  const craftedItem = materializeLegacyInventoryItemFromCraftResult(tableItem?._res || fallbackResult || tableItem, {
    quantity,
    entryIdFactory: craftedEntryIdFactory,
    abilityIdFactory: craftedAbilityIdFactory,
    acquiredAt,
  });
  const partyItem = materializePartyPoolItemFromLegacyInventoryItem(craftedItem, {
    quantity: craftedItem?.quantity || quantity,
    entryIdFactory,
    abilityIdFactory,
  });
  const result = stackItemAndResolveDestinationId(poolItems, partyItem);
  return {
    nextPartyItems: result.nextItems,
    partyItemEntryId: result.itemEntryId,
  };
};

export const applyShopCheckoutToCharAndShop = (
  charData = {},
  shopData = {},
  {
    shopId = shopData?.activeShopId || null,
    purchases = [],
    templates = shopData?.itemTemplates || {},
    currencyTiers = DEFAULT_CURRENCY_TIERS,
    isDM = false,
    currentCharId = charData?.id || null,
    transactionId = `shop_tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    acquiredAt = new Date().toISOString(),
    entryIdFactory,
    abilityIdFactory,
    logTimeFactory = () => new Date().toISOString(),
  } = {},
) => {
  const shops = Array.isArray(shopData?.shops) ? shopData.shops : [];
  const shop = shops.find((candidate) => candidate?.id === shopId) || null;
  if (!shop || !isShopAccessibleForPurchase(shop, { isDM, currentCharId })) return null;

  const listingRows = Array.isArray(shop.inventory) ? shop.inventory : [];
  const normalizedLines = normalizeShopPurchaseLines(purchases);
  if (normalizedLines.length === 0) return null;

  const groupsByListingId = new Map();
  for (const line of normalizedLines) {
    const listingIndex = listingRows.findIndex((listing) => {
      const listingId = resolveShopListingId(shop.id, listing);
      if (line.listingId) return line.listingId === listingId;
      return Boolean(line.itemId && line.itemId === listing.itemId);
    });
    if (listingIndex < 0) return null;

    const listing = listingRows[listingIndex];
    const listingId = resolveShopListingId(shop.id, listing);
    const rawTemplate = templates[listing.templateKey || listing.listingId] || templates[listing.itemId];
    const template = rawTemplate ? {
      ...rawTemplate,
      id: listing.itemId || rawTemplate.definitionId || rawTemplate.sourceDefinitionId || rawTemplate.id,
      definitionId: listing.itemId || rawTemplate.definitionId || rawTemplate.sourceDefinitionId || rawTemplate.id,
    } : null;
    if (!listingId || !template) return null;
    if (listing.tradeOnly === true) return null;
    if (!isListingVisibleForPurchase(listing, { isDM })) return null;
    if (
      line.expectedPrice != null
      && Number(line.expectedPrice) !== resolveAuthoritativeShopUnitPrice(listing)
    ) {
      return null;
    }
    if (
      line.expectedListingVersion != null
      && listing.version !== line.expectedListingVersion
    ) {
      return null;
    }

    const currentGroup = groupsByListingId.get(listingId) || {
      listing,
      listingIndex,
      listingId,
      template,
      quantity: 0,
    };
    currentGroup.quantity += line.quantity;
    groupsByListingId.set(listingId, currentGroup);
  }

  const groups = [...groupsByListingId.values()];
  for (const group of groups) {
    if (group.listing.stock !== null) {
      const finiteStock = Math.max(0, Math.trunc(Number(group.listing.stock) || 0));
      if (group.quantity > finiteStock) return null;
    }
  }

  const totalCost = groups.reduce((sum, group) => (
    sum + resolveAuthoritativeShopUnitPrice(group.listing) * group.quantity
  ), 0);
  const walletBase = walletToBaseValue(charData?.wallet || {}, currencyTiers);
  if (totalCost > walletBase) return null;

  let nextInventory = Array.isArray(charData?.inventory) ? charData.inventory : [];
  const logEntries = [];
  const purchasedLines = [];

  groups.forEach((group) => {
    const unitPrice = resolveAuthoritativeShopUnitPrice(group.listing);
    const lineTotal = unitPrice * group.quantity;
    const definitionId = group.template?.id || group.listing.itemId || null;
    const provenance = {
      sourceType: 'shop-purchase',
      transactionId,
      shopId: shop.id,
      shopName: shop.name || null,
      sellerName: shop.keeperName || shop.sellerName || null,
      listingId: group.listingId,
      definitionId,
      itemId: group.listing.itemId || definitionId,
      quantity: group.quantity,
      unitPricePaid: unitPrice,
      totalPaid: lineTotal,
      priceUnit: 'base',
      acquiredAt,
    };

    nextInventory = applyShopPurchaseToLegacyInventory(nextInventory, group.template, {
      quantity: group.quantity,
      entryIdFactory,
      abilityIdFactory,
      acquiredAt,
      extraFields: { provenance },
    });
    const itemEntryId = resolveInventoryEntryIdByProvenance(nextInventory, provenance);

    logEntries.push({
      item: group.template.name,
      qty: group.quantity,
      total: lineTotal,
      time: logTimeFactory(),
      char: charData?.name || '',
    });
    purchasedLines.push({
      itemEntryId,
      listingId: group.listingId,
      itemId: group.listing.itemId || null,
      definitionId,
      itemName: group.template.name,
      quantity: group.quantity,
      unitPrice,
      total: lineTotal,
      stockBefore: group.listing.stock,
      stockAfter: group.listing.stock === null
        ? null
        : Math.max(0, Math.trunc(Number(group.listing.stock) || 0) - group.quantity),
    });
  });

  const quantityByListingId = new Map(groups.map((group) => [group.listingId, group.quantity]));
  const nextShopInventory = listingRows.map((listing) => {
    const listingId = resolveShopListingId(shop.id, listing);
    const purchasedQty = quantityByListingId.get(listingId) || 0;
    if (!purchasedQty || listing.stock === null) return listing;
    return {
      ...listing,
      stock: Math.max(0, Math.trunc(Number(listing.stock) || 0) - purchasedQty),
      version: Number.isFinite(listing.version) ? listing.version + 1 : listing.version,
    };
  });
  const nextShop = { ...shop, inventory: nextShopInventory };
  const nextShopData = {
    ...shopData,
    shops: shops.map((candidate) => (candidate?.id === shop.id ? nextShop : candidate)),
    transactionLog: [...logEntries, ...(Array.isArray(shopData?.transactionLog) ? shopData.transactionLog : [])].slice(0, 50),
  };

  return {
    transactionId,
    shopId: shop.id,
    totalCost,
    itemCount: groups.reduce((sum, group) => sum + group.quantity, 0),
    logEntries,
    purchasedLines,
    nextShopInventory,
    nextShopData,
    nextCharData: {
      ...charData,
      wallet: baseToWalletValue(walletBase - totalCost, currencyTiers),
      inventory: nextInventory,
    },
  };
};

export const applyShopPurchaseToCharAndShop = (
  charData = {},
  shopData = {},
  {
    listingId = null,
    itemId = null,
    quantity = 1,
    ...options
  } = {},
) => applyShopCheckoutToCharAndShop(charData, shopData, {
  ...options,
  purchases: [{ listingId, itemId, quantity }],
});

export const applyShopSellBackToChar = (
  charData = {},
  {
    shopId = null,
    itemEntryId,
    quantity,
    payoutBase,
    currencyTiers = DEFAULT_CURRENCY_TIERS,
    transactionId = `shop_sell_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    soldAt = new Date().toISOString(),
    allowTradeOnlyCurrencySale = false,
  } = {},
) => {
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  if (item.tradeOnly === true && allowTradeOnlyCurrencySale !== true) return null;

  const haveQty = Math.max(1, Number(item.quantity) || 1);
  const moveQty = quantity == null
    ? haveQty
    : Math.trunc(Number(quantity));
  if (!Number.isFinite(moveQty) || moveQty < 1 || moveQty > haveQty) return null;
  if (item.equipped === true && moveQty >= haveQty) return null;

  const normalizedPayoutBase = Math.trunc(Number(payoutBase));
  if (!Number.isFinite(normalizedPayoutBase) || normalizedPayoutBase < 0) return null;

  const nextInventory = moveQty >= haveQty
    ? inventory.filter((candidate) => candidate?.id !== item.id)
    : inventory.map((candidate) => (
      candidate?.id === item.id
        ? { ...candidate, quantity: haveQty - moveQty }
        : candidate
    ));
  const walletBase = walletToBaseValue(charData?.wallet || {}, currencyTiers);
  const sourceSnapshot = cloneValue({
    ...item,
    quantity: moveQty,
    equipped: false,
    favorited: false,
  });
  const sourceProvenance = item.provenance
    ? cloneValue(item.provenance)
    : (item.lootSource ? { sourceType: 'loot-award', lootSource: cloneValue(item.lootSource) } : null);

  const soldLine = {
    itemEntryId: item.id,
    itemId: item.itemId || null,
    definitionId: item.itemId || item.definitionId || null,
    itemName: item.name || 'Item',
    quantity: moveQty,
    unitPayoutBase: moveQty > 0 ? normalizedPayoutBase / moveQty : normalizedPayoutBase,
    totalPayoutBase: normalizedPayoutBase,
    sourceQuantityBefore: haveQty,
    sourceQuantityAfter: Math.max(0, haveQty - moveQty),
    removedFromInventory: moveQty >= haveQty,
    tradeOnly: item.tradeOnly === true,
    basePrice: Number.isFinite(Number(item.basePrice)) ? Number(item.basePrice) : null,
    quality: item.quality || null,
    sourceProvenance,
    sourceItemSnapshot: sourceSnapshot,
  };

  return {
    transactionId,
    shopId,
    payoutBase: normalizedPayoutBase,
    itemCount: moveQty,
    soldLines: [soldLine],
    soldItemSnapshot: sourceSnapshot,
    movedQuantity: moveQty,
    removedFromInventory: moveQty >= haveQty,
    nextCharData: {
      ...charData,
      wallet: baseToWalletValue(walletBase + normalizedPayoutBase, currencyTiers),
      inventory: nextInventory,
    },
  };
};

export const applyPartyDepositToCharAndPool = (
  charData = {},
  currentPartyInventory = { items: [] },
  {
    itemEntryId,
    quantity,
    entryIdFactory,
    abilityIdFactory,
  } = {},
) => {
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  const haveQty = Math.max(1, item.quantity || 1);
  const moveQty = quantity == null
    ? haveQty
    : Math.max(1, Math.min(haveQty, Math.trunc(quantity) || 1));
  if (item.equipped === true && moveQty >= haveQty) return null;

  const partyItems = Array.isArray(currentPartyInventory.items) ? currentPartyInventory.items : [];
  const partyItem = materializePartyPoolItemFromLegacyInventoryItem(item, {
    quantity: moveQty,
    entryIdFactory,
    abilityIdFactory,
  });
  const existingPartyItem = partyItems.find((candidate) => canStackLegacyInventoryItems(candidate, partyItem));
  const nextPartyItems = partyItem
    ? stackLegacyInventoryItem(partyItems, partyItem)
    : applyLegacyInventoryTransferToPartyPool(partyItems, item, {
        quantity: moveQty,
        entryIdFactory,
        abilityIdFactory,
      });
  const nextInventory = moveQty >= haveQty
    ? inventory.filter((candidate) => candidate?.id !== item.id)
    : inventory.map((candidate) => (
      candidate?.id === item.id
        ? { ...candidate, quantity: haveQty - moveQty }
        : candidate
    ));

  return {
    movedItem: item,
    movedQuantity: moveQty,
    partyItemEntryId: existingPartyItem?.id || partyItem?.id || null,
    removedFromInventory: moveQty >= haveQty,
    nextCharData: {
      ...charData,
      inventory: nextInventory,
    },
    nextPartyInventory: {
      ...currentPartyInventory,
      items: nextPartyItems,
    },
  };
};

export const applyCharToCharTransfer = (
  sourceCharData = {},
  targetCharData = {},
  {
    itemEntryId,
    quantity,
    sourceName = null,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  const sourceInventory = Array.isArray(sourceCharData.inventory) ? sourceCharData.inventory : [];
  const targetInventory = Array.isArray(targetCharData.inventory) ? targetCharData.inventory : [];
  const item = sourceInventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  const haveQty = Math.max(1, item.quantity || 1);
  const moveQty = quantity == null
    ? haveQty
    : Math.max(1, Math.min(haveQty, Math.trunc(quantity) || 1));
  if (item.equipped === true && moveQty >= haveQty) return null;

  const nextSourceInventory = moveQty >= haveQty
    ? sourceInventory.filter((candidate) => candidate?.id !== item.id)
    : sourceInventory.map((candidate) => (
      candidate?.id === item.id
        ? { ...candidate, quantity: haveQty - moveQty }
        : candidate
    ));

  const transferredItem = materializeTransferredLegacyInventoryItem(item, {
    quantity: moveQty,
    sourceName: sourceName || sourceCharData.name || null,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
  });
  const existingTargetItem = targetInventory.find((candidate) => canStackLegacyInventoryItems(candidate, transferredItem));
  const nextTargetInventory = stackLegacyInventoryItem(targetInventory, transferredItem);
  const targetItemEntryId = existingTargetItem?.id || transferredItem.id || null;

  return {
    movedItem: item,
    movedQuantity: moveQty,
    targetItemEntryId,
    removedFromSourceInventory: moveQty >= haveQty,
    nextSourceCharData: {
      ...sourceCharData,
      inventory: nextSourceInventory,
    },
    nextTargetCharData: {
      ...targetCharData,
      inventory: nextTargetInventory,
    },
  };
};

export const applyPartyTakeToCharAndPool = (
  charData = {},
  currentPartyInventory = { items: [] },
  {
    itemEntryId,
    quantity,
    entryIdFactory = () => `taken_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    abilityIdFactory,
  } = {},
) => {
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const poolItems = Array.isArray(currentPartyInventory.items) ? currentPartyInventory.items : [];
  const poolItem = poolItems.find((candidate) => candidate?.id === itemEntryId);
  if (!poolItem) return null;

  const haveQty = Math.max(1, poolItem.quantity || 1);
  const moveQty = quantity == null
    ? haveQty
    : Math.max(1, Math.min(haveQty, Math.trunc(quantity) || 1));

  const nextPartyItems = moveQty >= haveQty
    ? poolItems.filter((candidate) => candidate?.id !== poolItem.id)
    : poolItems.map((candidate) => (
      candidate?.id === poolItem.id
        ? { ...candidate, quantity: haveQty - moveQty }
        : candidate
    ));

  const takenItem = materializeTransferredLegacyInventoryItem(poolItem, {
    quantity: moveQty,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt: poolItem.addedDate || new Date().toISOString(),
  });
  const existingTargetItem = inventory.find((candidate) => canStackLegacyInventoryItems(candidate, takenItem));
  const nextInventory = stackLegacyInventoryItem(inventory, takenItem);
  const targetItemEntryId = existingTargetItem?.id || takenItem.id || null;

  return {
    movedItem: poolItem,
    movedQuantity: moveQty,
    targetItemEntryId,
    removedFromPartyPool: moveQty >= haveQty,
    nextCharData: {
      ...charData,
      inventory: nextInventory,
    },
    nextPartyInventory: {
      ...currentPartyInventory,
      items: nextPartyItems,
    },
  };
};

export const applyCharInventoryToCraftingTableTransfer = (
  charData = {},
  {
    itemEntryId,
    quantity,
  } = {},
) => {
  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const item = inventory.find((candidate) => candidate?.id === itemEntryId);
  if (!item) return null;

  const haveQty = Math.max(1, item.quantity || 1);
  const moveQty = normalizeMoveQuantity(quantity, haveQty);
  if (item.equipped === true && moveQty >= haveQty) return null;

  const nextCraftingTable = applyLegacyInventoryTransferToCraftingTable(
    charData.craftingTable,
    item,
    { quantity: moveQty },
  );
  if (!nextCraftingTable) return null;

  const nextInventory = moveQty >= haveQty
    ? inventory.filter((candidate) => candidate?.id !== item.id)
    : inventory.map((candidate) => (
      candidate?.id === item.id
        ? { ...candidate, quantity: haveQty - moveQty }
        : candidate
    ));

  return {
    movedItem: item,
    movedQuantity: moveQty,
    removedFromInventory: moveQty >= haveQty,
    nextCharData: {
      ...charData,
      inventory: nextInventory,
      craftingTable: nextCraftingTable,
    },
  };
};

export const applyCraftOutputToCharInventory = (
  charData = {},
  {
    result,
    quantity = 1,
    consumedIngredients = [],
    entryIdFactory,
    abilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  const nextCraftingTable = consumeCraftingIngredients(charData.craftingTable, consumedIngredients);
  if (!nextCraftingTable) return null;

  const craftedItem = materializeLegacyInventoryItemFromCraftResult(result, {
    quantity,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
  });
  if (!craftedItem) return null;

  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const stackResult = stackItemAndResolveDestinationId(inventory, craftedItem);

  return {
    movedItem: craftedItem,
    movedQuantity: craftedItem.quantity || Math.max(1, quantity || 1),
    itemEntryId: stackResult.itemEntryId,
    nextCharData: {
      ...charData,
      inventory: stackResult.nextItems,
      craftingTable: nextCraftingTable,
    },
  };
};

export const applyCraftOutputToPartyPool = (
  charData = {},
  currentPartyInventory = { items: [] },
  {
    result,
    quantity = 1,
    consumedIngredients = [],
    craftedEntryIdFactory,
    craftedAbilityIdFactory,
    partyEntryIdFactory,
    partyAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  const nextCraftingTable = consumeCraftingIngredients(charData.craftingTable, consumedIngredients);
  if (!nextCraftingTable) return null;

  const currentItems = Array.isArray(currentPartyInventory.items) ? currentPartyInventory.items : [];
  const craftedItem = materializeLegacyInventoryItemFromCraftResult(result, {
    quantity,
    entryIdFactory: craftedEntryIdFactory,
    abilityIdFactory: craftedAbilityIdFactory,
    acquiredAt,
  });
  const partyItem = materializePartyPoolItemFromLegacyInventoryItem(craftedItem, {
    quantity: craftedItem?.quantity || quantity,
    entryIdFactory: partyEntryIdFactory,
    abilityIdFactory: partyAbilityIdFactory,
  });
  const stackResult = stackItemAndResolveDestinationId(currentItems, partyItem);
  if (!partyItem) return null;

  return {
    movedItem: result,
    movedQuantity: Math.max(1, quantity || 1),
    partyItemEntryId: stackResult.itemEntryId,
    nextCharData: {
      ...charData,
      craftingTable: nextCraftingTable,
    },
    nextPartyInventory: {
      ...currentPartyInventory,
      items: stackResult.nextItems,
    },
  };
};

export const applyCraftingTableRowToCharInventory = (
  charData = {},
  {
    tableItemId,
    quantity = null,
    fallbackResult = null,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  if (!tableItemId) return null;
  const table = charData.craftingTable || {};
  const tableItem = resolveCraftingTableSourceItem(table, tableItemId, fallbackResult);
  const decrementResult = decrementCraftingTableRow(table, tableItemId, quantity);
  if (!tableItem || !decrementResult) return null;

  const movedItem = materializeCraftingTableSourceForInventory(tableItem, {
    quantity: decrementResult.movedQuantity,
    fallbackResult,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
  });
  if (!movedItem) return null;

  const inventory = Array.isArray(charData.inventory) ? charData.inventory : [];
  const stackResult = stackItemAndResolveDestinationId(inventory, movedItem);

  return {
    movedItem,
    movedQuantity: decrementResult.movedQuantity,
    itemEntryId: stackResult.itemEntryId,
    removedFromCraftingTable: decrementResult.removedFromCraftingTable,
    nextCharData: {
      ...charData,
      inventory: stackResult.nextItems,
      craftingTable: decrementResult.nextCraftingTable,
    },
  };
};

export const applyCraftingTableRowToPartyPool = (
  charData = {},
  currentPartyInventory = { items: [] },
  {
    tableItemId,
    quantity = null,
    fallbackResult = null,
    entryIdFactory,
    abilityIdFactory,
    partyEntryIdFactory,
    partyAbilityIdFactory,
    craftedEntryIdFactory,
    craftedAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  if (!tableItemId) return null;
  const table = charData.craftingTable || {};
  const tableItem = resolveCraftingTableSourceItem(table, tableItemId, fallbackResult);
  const decrementResult = decrementCraftingTableRow(table, tableItemId, quantity);
  if (!tableItem || !decrementResult) return null;

  const currentItems = Array.isArray(currentPartyInventory.items) ? currentPartyInventory.items : [];
  const partyResult = materializeCraftingTableSourceForPartyPool(currentItems, tableItem, {
    quantity: decrementResult.movedQuantity,
    fallbackResult,
    entryIdFactory: partyEntryIdFactory || entryIdFactory,
    abilityIdFactory: partyAbilityIdFactory || abilityIdFactory,
    craftedEntryIdFactory,
    craftedAbilityIdFactory,
    acquiredAt,
  });
  if (!partyResult?.partyItemEntryId) return null;

  return {
    movedItem: tableItem,
    movedQuantity: decrementResult.movedQuantity,
    partyItemEntryId: partyResult.partyItemEntryId,
    removedFromCraftingTable: decrementResult.removedFromCraftingTable,
    nextCharData: {
      ...charData,
      craftingTable: decrementResult.nextCraftingTable,
    },
    nextPartyInventory: {
      ...currentPartyInventory,
      items: partyResult.nextPartyItems,
    },
  };
};
